import React from 'react';

import {
  StyleSheet,
  View,
  TextInput,
  RegisteredStyle,
  TextInputProps,
  TextStyle,
  ViewStyle,
  TouchableOpacity
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import fontMaker from '../../utils/font';

type AuthFieldProps = {
  /** If true, the form field is marked as valid (with accompanying styling) */
  isValid: boolean;
  /** If true, the password will be shown */
  isPasswordVisible?: boolean;
  /** The icon on the left side for styling */
  icon: string;
  /** Additional style object to override default style */
  style?: RegisteredStyle<any>;
  /** If true, no input will be allowed on the field */
  disabled: boolean;
  /** Handle - show or hide  */
  onTogglePasswordVisibility?: () => void;
} & TextInputProps;

/**
 * A form field specifically for Auth flow.
 * Most functionality is mimicked from TextInput.
 */
const AuthField = React.forwardRef((props: AuthFieldProps, ref: any) => {
  const [isFocused, setIsFocused] = React.useState<boolean>(false);
  const iconStyles = [];
  if (props.icon === 'lock' || props.icon === 'email' || props.icon === 'phone') {
    iconStyles.push({ marginHorizontal: 4 });
  }

  const containerStyles = [styles.authFieldContainer];
  if (!props.isValid) {
    containerStyles.push(styles.authFieldInvalid as any);
  } else if (isFocused) {
    containerStyles.push(styles.authFieldFocused as any);
  } else {
    containerStyles.push(styles.authFieldDefault as any);
  }

  const inputStyles = [styles.input];
  if (props.icon === 'lock') {
    inputStyles.push(styles.inputWithTrailingIcon as any);
  }
  if (props.icon === 'email' || props.icon === 'phone') {
    inputStyles.push(styles.inputWithLeadingIcon as any);
  }

  const showIcon = props.icon !== 'lock' && props.icon !== 'blank';

  return (
    <View style={containerStyles}>
      {showIcon && <Icon name={props.icon} size={18} color="#98A2B3" style={iconStyles} />}
      {props.icon === 'lock' ? (
        <View style={styles.eyeContainer}>
          <TouchableOpacity onPress={props.onTogglePasswordVisibility}>
            <Icon
              name={props.isPasswordVisible ? 'eye' : 'eye-off'}
              color="#98A2B3"
              size={18}
              style={styles.eyeInput}
            />
          </TouchableOpacity>
        </View>
      ) : null}
      <TextInput
        {...props}
        ref={ref}
        style={inputStyles}
        placeholderTextColor={props.placeholderTextColor || '#98A2B3'}
        onFocus={event => {
          setIsFocused(true);
          if (props.onFocus) {
            props.onFocus(event);
          }
        }}
        onBlur={event => {
          setIsFocused(false);
          if (props.onBlur) {
            props.onBlur(event);
          }
        }}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  eyeContainer: {
    position: 'absolute',
    right: 0,
    backgroundColor: '#FFFFFF',
    width: 40,
    height: 28,
    zIndex: 2
  },
  authFieldContainer: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: '100%',
    borderWidth: 1,
    borderRadius: 12,
    minHeight: 20,
    paddingVertical: 14,
    paddingHorizontal: 18,
    marginBottom: 16,
    backgroundColor: '#FFFFFF',
    shadowColor: '#101828',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.03,
    shadowRadius: 3,
    elevation: 1
  },
  authFieldDefault: {
    borderColor: '#D0D5DD'
  },
  authFieldFocused: {
    borderColor: '#22C55E',
    backgroundColor: '#FFFFFF',
    shadowColor: '#22C55E',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.14,
    shadowRadius: 10,
    elevation: 3
  },
  authFieldInvalid: {
    borderColor: '#FF354A'
  },
  input: {
    ...fontMaker({ weight: 'Regular' }),
    flex: 1,
    height: 24,
    fontSize: 16,
    color: '#101828'
  },
  inputWithLeadingIcon: {
    marginLeft: 10,
    paddingLeft: 4
  },
  inputWithTrailingIcon: {
    paddingRight: 36
  },
  eyeInput: {
    position: 'absolute',
    right: 12,
    top: 4
  }
});

export default AuthField;
